import { useState, useEffect, useCallback } from 'react';
import { shuffle } from '../utils/shuffle';
import { rateDifficulty, getNextWordQueue, getSRSData, getSessionStats } from '../utils/srs';

/**
 * Custom hook for managing flashcard study sessions
 *
 * Handles:
 * - Loading and shuffling words
 * - Tracking current card index
 * - Recording difficulty ratings
 * - Session progress and statistics
 * - Navigation between cards
 *
 * @param {Array} initialWords - Array of word objects to study
 * @param {boolean} shouldShuffle - Whether to shuffle the deck (default: true)
 * @returns {Object} - Session state and controls
 *
 * PHASE 2 TODO:
 * - Fetch words from backend API instead of static JSON
 * - Sync session progress to server in real-time
 * - Add user authentication and multi-device sync
 */
export function useFlashcardSession(initialWords = [], shouldShuffle = true) {
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reviewedWords, setReviewedWords] = useState([]);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);

  // Initialize session with words. The `!sessionComplete` gate stops the
  // session from immediately re-initialising once it ends — the completion
  // effect flips sessionActive to false, which would otherwise re-trigger
  // this effect and replace the just-finished session with a new one.
  useEffect(() => {
    if (initialWords.length > 0 && !sessionActive && !sessionComplete) {
      const srsData = getSRSData();
      const prioritizedWords = getNextWordQueue(initialWords, srsData);
      const finalWords = shouldShuffle ? shuffle(prioritizedWords) : prioritizedWords;

      setWords(finalWords);
      setCurrentIndex(0);
      setReviewedWords([]);
      setSessionActive(true);
      setSessionComplete(false);
    }
  }, [initialWords, shouldShuffle, sessionActive, sessionComplete]);

  // Get current word
  const currentWord = words[currentIndex] || null;

  // Check if session is complete
  useEffect(() => {
    if (sessionActive && currentIndex >= words.length && words.length > 0) {
      setSessionComplete(true);
      setSessionActive(false);
    }
  }, [currentIndex, words.length, sessionActive]);

  /**
   * Rate the current word and move to next
   *
   * @param {string} rating - One of: 'again', 'hard', 'good', 'easy'
   */
  const rateCurrentWord = useCallback((rating) => {
    if (!currentWord) return;

    // Record the rating in SRS system
    rateDifficulty(currentWord.id, rating);

    // Add to reviewed words with rating
    const reviewedWord = {
      ...currentWord,
      rating,
      reviewedAt: Date.now()
    };

    setReviewedWords(prev => [...prev, reviewedWord]);

    // Move to next card
    setCurrentIndex(prev => prev + 1);
  }, [currentWord]);

  /**
   * Navigate to next card without rating. On the final card this advances
   * past the end (currentIndex === words.length) so the completion effect
   * can fire — without that, sessionComplete would never become true and
   * the user would be stuck on the last card with no way to finish.
   */
  const nextCard = useCallback(() => {
    if (currentIndex < words.length) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, words.length]);

  /**
   * Navigate to previous card
   */
  const previousCard = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  /**
   * Restart the session with the same words
   */
  const restartSession = useCallback(() => {
    setCurrentIndex(0);
    setReviewedWords([]);
    setSessionActive(true);
    setSessionComplete(false);
  }, []);

  /**
   * Reset all session state. Used when leaving Study or starting a new session
   * with different words — without this, the init effect's `!sessionActive`
   * gate prevents a new word list from replacing the old one.
   */
  const resetSession = useCallback(() => {
    setWords([]);
    setCurrentIndex(0);
    setReviewedWords([]);
    setSessionActive(false);
    setSessionComplete(false);
  }, []);

  /**
   * Start a new session with different words
   *
   * @param {Array} newWords - New word set
   */
  const startNewSession = useCallback((newWords) => {
    const srsData = getSRSData();
    const prioritizedWords = getNextWordQueue(newWords, srsData);
    const finalWords = shouldShuffle ? shuffle(prioritizedWords) : prioritizedWords;

    setWords(finalWords);
    setCurrentIndex(0);
    setReviewedWords([]);
    setSessionActive(true);
    setSessionComplete(false);
  }, [shouldShuffle]);

  /**
   * Get session progress (0-100)
   */
  const progress = words.length > 0
    ? Math.round(((currentIndex + 1) / words.length) * 100)
    : 0;

  /**
   * Get session statistics
   */
  const stats = getSessionStats(reviewedWords);

  return {
    // Current state
    currentWord,
    currentIndex,
    totalWords: words.length,
    progress,
    sessionActive,
    sessionComplete,

    // Actions
    rateCurrentWord,
    nextCard,
    previousCard,
    restartSession,
    resetSession,
    startNewSession,

    // Session data
    reviewedWords,
    stats,

    // Helper flags
    isFirstCard: currentIndex === 0,
    isLastCard: currentIndex === words.length - 1,
    // hasNext stays true on the last card so the user can click Next/Finish to
    // advance past the deck and trigger the completion effect.
    hasNext: currentIndex < words.length,
    hasPrevious: currentIndex > 0,
  };
}
